import React, { useRef, useState } from 'react';
import {
  Actions,
  BackContainer,
  ButtonsContainer,
  Container,
  ContinueButton,
  Row,
  StyledButton,
  Subtitle,
  TestButton,
  TestVoiceContainer,
  TitleContainer,
  VoiceButton,
  VoiceNameContainer,
  VoiceNameContent,
  VoiceUploadContainer,
} from './InstantVoiceUpload.styles';
import { ReactComponent as ChevronLeftIcon } from '../../../assets/icons/chevron_left.svg';
import { Text14Light, Text16Regular, Text18Bold } from '../../utils/Text/Text.styles';
import { UPLOAD_FILE_CONSTRAINTS, UPLOAD_MEDIA_FILE_ERROR_TYPE } from '../../../utils/constants';
import ErrorContainer from '../../utils/ErrorContainer/ErrorContainer';
import UploadMediaButton from '../../UploadMediaButton/UploadMediaButton';
import MediaRecorderComponent from '../../MediaRecorderComponent/MediaRecorderComponent';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { ReactComponent as PlayIcon } from '../../../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../../../assets/icons/pause-circle.svg';
import { ReactComponent as PlayWhiteIcon } from '../../../assets/icons/play-white.svg';
import { selectInfluencerRequestActionsPending } from '../../../store/slices/influencerRequest/slice';
import { useDispatch, useSelector } from 'react-redux';
import { setUserStatus } from '../../../store/slices/user/slice';
import {
  saveVoiceElevenLabs,
  testVoiceElevenLabs,
  uploadAudioElevenLabs,
} from '../../../store/slices/influencerRequest/asyncThunks';
import { notifyError, notifyInfo } from '../../../utils/notify';
import Input from '../../utils/inputs/Input/Input';

const voices = [
  { title: 'Voice 1', stability: 0 },
  { title: 'Voice 2', stability: 0.5 },
  { title: 'Voice 3', stability: 1 },
];

const InstantVoiceUpload = ({ setSelectedTab }) => {
  const dispatch = useDispatch();
  const {
    testVoice: testVoicePending,
    uploadAudio: uploadAudioPending,
    saveVoice: saveVoicePending,
  } = useSelector(selectInfluencerRequestActionsPending);

  const ERROR_MESSAGES = {
    [UPLOAD_MEDIA_FILE_ERROR_TYPE.FILE_SIZE]: `File size is too large (max ${UPLOAD_FILE_CONSTRAINTS.AUDIO.MAX_SIZE_IN_MB}MB)`,
    [UPLOAD_MEDIA_FILE_ERROR_TYPE.FILE_TYPE]: `Invalid file type (allowed: ${UPLOAD_FILE_CONSTRAINTS.AUDIO.ALLOWED_EXTENSIONS_TEXT})`,
    [UPLOAD_MEDIA_FILE_ERROR_TYPE.AUDIO_DURATION]: `Audio duration is less than ${UPLOAD_FILE_CONSTRAINTS.AUDIO.MIN_DURATION_IN_SECONDS} seconds`,
  };
  const audioRef = useRef(null);

  const [errors, setErrors] = useState([]);
  const [audios, setAudios] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(voices[0]);
  const [voiceId, setVoiceId] = useState(null);
  const [message, setMessage] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);

  const onContinueClick = () => {
    dispatch(saveVoiceElevenLabs({ stability: selectedVoice.stability }))
      .unwrap()
      .then(({ userStatus }) => {
        dispatch(setUserStatus(userStatus));
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const onUploadClick = () => {
    dispatch(uploadAudioElevenLabs({ audio: audios[0] }))
      .unwrap()
      .then(({ voiceId }) => {
        setVoiceId(voiceId);
        notifyInfo('Audio uploaded successfully');
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const onDeleteVoice = () => {
    setAudios([]);
    setErrors([]);
    setVoiceId(null);
    onPauseVoice();
  };

  const onPlayVoice = () => {
    if (audios[0]) {
      if (!audioRef.current) {
        const audio = new Audio(audios[0].url);
        audioRef.current = audio;
        audio.onended = () => {
          setIsPlaying(false);
          audioRef.current = null;
        };
      }

      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const onPauseVoice = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const onTestClick = () => {
    if (!message.trim()) {
      notifyError('Please enter text');
      return;
    }
    dispatch(testVoiceElevenLabs({ message, stability: selectedVoice.stability }))
      .unwrap()
      .then(({ speech }) => {
        const audioBlob = new Blob([new Uint8Array(speech.data)], { type: 'audio/mp3' });
        const receivedVoiceUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(receivedVoiceUrl);
        audio.play();
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  return (
    <Container>
      <BackContainer onClick={() => setSelectedTab(null)}>
        <ChevronLeftIcon />
      </BackContainer>
      <TitleContainer>
        <Text18Bold>Instant Cloning</Text18Bold>
        <Subtitle>Please record your voice or upload audio recording of your voice.</Subtitle>
      </TitleContainer>
      {audios[0] ? (
        <VoiceUploadContainer>
          <VoiceNameContainer>
            <VoiceNameContent>
              <Text14Light>{audios[0].file.name || 'Uploaded Audio'}</Text14Light>
              <Actions>
                {isPlaying ? (
                  <PauseIcon onClick={onPauseVoice} width={20} height={20} color="#1672BA" />
                ) : (
                  <PlayIcon onClick={onPlayVoice} width={20} height={20} color="#1672BA" />
                )}
                <CloseIcon onClick={onDeleteVoice} width={20} height={20} color="#1672BA" />
              </Actions>
            </VoiceNameContent>
            {errors[0] && <ErrorContainer errorText={ERROR_MESSAGES[errors[0].type]} />}
          </VoiceNameContainer>
          {!voiceId && (
            <StyledButton
              title="Upload"
              onClick={onUploadClick}
              isLoading={uploadAudioPending}
              disabled={!audios[0] || errors[0]}
            />
          )}
        </VoiceUploadContainer>
      ) : (
        <>
          <UploadMediaButton
            type={UPLOAD_FILE_CONSTRAINTS.TYPE.AUDIO}
            onChange={files => {
              setAudios([...audios, ...files]);
              audioRef.current = null;
            }}
            setError={setErrors}
          />
          <MediaRecorderComponent
            setRecordedVoice={file => {
              setAudios([file]);
              if (file.duration < UPLOAD_FILE_CONSTRAINTS.AUDIO.MIN_DURATION_IN_SECONDS) {
                setErrors([{ type: UPLOAD_MEDIA_FILE_ERROR_TYPE.AUDIO_DURATION }]);
              }
              audioRef.current = null;
            }}
            recordedVoice={audios[0]}
          />
        </>
      )}
      {voiceId && (
        <TestVoiceContainer>
          <Text16Regular>Select Voice</Text16Regular>
          <ButtonsContainer>
            {voices.map(voice => {
              return (
                <VoiceButton
                  key={voice.title}
                  $selected={selectedVoice.title === voice.title}
                  onClick={() => setSelectedVoice(voice)}>
                  {voice.title}
                </VoiceButton>
              );
            })}
          </ButtonsContainer>
          <Row>
            <Input
              value={message}
              onChange={e => setMessage(e.target.value)}
              label="Enter text to test your voice"
              placeholder="Enter text here"
            />
            <TestButton title="Play" icon={<PlayWhiteIcon />} isLoading={testVoicePending} onClick={onTestClick} />
          </Row>
        </TestVoiceContainer>
      )}
      <ContinueButton
        title={voiceId ? `Continue with ${selectedVoice.title}` : 'Continue'}
        onClick={onContinueClick}
        isLoading={saveVoicePending}
        disabled={!voiceId}
      />
    </Container>
  );
};

export default InstantVoiceUpload;
